import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { DateRangePicker } from 'react-dates';
import { useIntl } from 'react-intl';

import { slugify } from '../../../helpers';
import {
  getDateRanges,
  getDateRange,
} from '../../../helpers/DateTime';

const AdminReportsFilterDates = ({
  settings,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
  period,
  setPeriod,
  granularity,
}) => {
  const intl = useIntl();

  const [filter, setFilter] = useState('30-days');
  const [focusedInput, setFocusedInput] = useState(null);

  const getDates = (range) => {
    if (range.id === 'custom') {
      return {
        from: dateFrom,
        to: dateTo,
      };
    }

    return {
      from: moment().utc().subtract(range.days, 'days'),
      to: moment().utc().subtract(),
    };
  };

  const handleChangeDateRange = (e) => {
    const { value } = e.target;

    const range = getDateRange(value, intl);
    if (range) {
      setFilter(range.id);
      const dates = getDates(range);
      setDateFrom(dates.from);
      setDateTo(dates.to);
    }
  };

  const handleChangeDates = (dates) => {
    if (dates.startDate && dates.endDate) {
      setDateFrom(dates.startDate);
      setDateTo(dates.endDate);
    }
  };

  const handleChangePeriod = (e) => {
    const { value } = e.target;
    setPeriod(value);
  };

  // eslint-disable-next-line react/prop-types
  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => {
    const min = settings.minFileDate;

    const returnYears = () => {
      const max = moment().format('YYYY-MM-DD');
      const years = [];
      for (
        let i = parseInt(moment(min).format('YYYY'));
        i <= parseInt(moment(max).format('YYYY'));
        i += 1
      ) {
        years.push(<option key={i} value={i}>{i}</option>);
      }
      return years;
    };

    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div>
          <Input
            type="select"
            className="custom-select"
            bsSize="sm"
            value={
              moment(month).isBefore(moment(min)) ?
                moment(min).month() :
                month.month()
            }
            onChange={(e) => {
              if (month.isBefore(moment(min))) {
                onMonthSelect(moment(min), moment(min).month());
              } else {
                onMonthSelect(month, e.target.value);
              }
            }}
          >
            {moment.months().map((label, value) => (
              <option key={slugify(label)} value={value}>{label}</option>
            ))}
          </Input>
        </div>
        <div>
          <Input
            type="select"
            className="custom-select"
            bsSize="sm"
            value={month.year()}
            onChange={(e) => {
              onYearSelect(month, e.target.value);
            }}
          >
            {returnYears()}
          </Input>
        </div>
      </div>
    );
  };

  return (
    <div className="report-date-filter-wrapper form-inline">
      <FormGroup>
        <Label for="date-range">
          {intl.formatMessage({ defaultMessage: 'Period:' })}
        </Label>
        <Input
          type="select"
          className="custom-select"
          id="date-range"
          name="date-range"
          bsSize="sm"
          defaultValue={filter}
          onChange={handleChangeDateRange}
        >
          {getDateRanges(intl).map(r => (
            <option
              key={r.id}
              value={r.id}
            >
              {r.label}
            </option>
          ))}
        </Input>
      </FormGroup>
      {filter === 'custom' && (
        <FormGroup className="date-filter">
          <DateRangePicker
            startDate={dateFrom}
            startDateId="report_date_from"
            endDate={dateTo}
            endDateId="report_date_to"
            numberOfMonths={2}
            onDatesChange={handleChangeDates}
            hideKeyboardShortcutsPanel={true}
            noBorder={true}
            customArrowIcon={<i className="far fa-long-arrow-right" />}
            isOutsideRange={() => false}
            focusedInput={focusedInput}
            onFocusChange={fi => setFocusedInput(fi)}
            renderMonthElement={params => (renderMonthElement(params))}
          />
        </FormGroup>
      )}
      {granularity && (
        <FormGroup className="period-filter">
          <Input
            type="select"
            className="custom-select"
            bsSize="sm"
            onChange={handleChangePeriod}
            defaultValue={period}
          >
            <option value="by_day">
              {intl.formatMessage({ defaultMessage: 'by day' })}
            </option>
            <option value="by_week">
              {intl.formatMessage({ defaultMessage: 'by week' })}
            </option>
            <option value="by_month">
              {intl.formatMessage({ defaultMessage: 'by month' })}
            </option>
            <option value="by_year">
              {intl.formatMessage({ defaultMessage: 'by year' })}
            </option>
          </Input>
        </FormGroup>
      )}
    </div>
  );
};

AdminReportsFilterDates.propTypes = {
  settings: PropTypes.object,
  dateFrom: PropTypes.object.isRequired,
  dateTo: PropTypes.object.isRequired,
  period: PropTypes.string,
  granularity: PropTypes.bool,
  setDateFrom: PropTypes.func.isRequired,
  setDateTo: PropTypes.func.isRequired,
  setPeriod: PropTypes.func,
};

AdminReportsFilterDates.defaultProps = {
  settings: null,
  period: '',
  granularity: false,
  setPeriod: () => {},
};

export default AdminReportsFilterDates;
